// Helpers operating on canonical FlightOffer.travelerPricings[] entries
// and the form-side `traveler_details` shape. Pure transforms / lookups.

import moment from 'moment';

import { get_birth_date_constraints, log } from '@/utilities/commons';

const TRAVELER_TYPES = {
  ADULT: 'adult',
  CHILD: 'child',
  TEEN: 'teen',
  HELD_INFANT: 'held_infant',
  SEATED_INFANT: 'seated_infant',
};

const INFANT_TYPES = new Set([
  TRAVELER_TYPES.HELD_INFANT,
  TRAVELER_TYPES.SEATED_INFANT,
]);

const ADULT_AGE = 18;

export const traveler_type = (raw) => {
  const traveler = raw || {};
  return String(
    traveler.traveler_type || traveler.travelerType || traveler.type
  ).toLowerCase();
};

export const traveler_name = (traveler) => {
  if (!traveler) {
    return '';
  }
  const { firstname, lastname, name, id } = traveler;
  if (firstname && lastname) {
    return `${firstname} ${lastname}`;
  }
  if (name) {
    return Object.values(name).join(' ');
  }
  return `P${id}`;
};

export const traveler_initials = (traveler) => {
  const parts = traveler_name(traveler).split(' ').filter(Boolean);
  if (!parts.length) {
    return '';
  }

  const safe = [parts[0]];
  if (parts.length > 1) {
    safe.push(parts[parts.length - 1]);
  }

  return safe
    .map((part) => String(part).trim().charAt(0))
    .join('')
    .toUpperCase();
};

export const is_infant = (traveler) =>
  !!traveler && INFANT_TYPES.has(traveler_type(traveler));

export const is_child = (traveler) =>
  !!traveler && traveler_type(traveler) === TRAVELER_TYPES.CHILD;

export const is_teen = (traveler) =>
  !!traveler && traveler_type(traveler) === TRAVELER_TYPES.TEEN;

export const is_adult = (traveler) =>
  !!traveler && traveler_type(traveler) === TRAVELER_TYPES.ADULT;

/**
 * Shallow copy with every string field trimmed of edge whitespace. Pasted
 * names, emails and phone numbers routinely carry leading/trailing spaces
 * that Amadeus /orders and Kyte /book reject at PNR creation. Run once at
 * checkout submit — never bind it live (v-model.trim strips spaces mid-word,
 * breaking multi-token names like "De La Cruz").
 *
 * @param {object} record a traveler_details entry or contact_details
 * @returns {object}
 */
export const trim_traveler_strings = (record) => {
  const safe = { ...(record || {}) };
  return Object.entries(safe).reduce((trimmed, entry) => {
    const [key, value] = entry;
    if (typeof value === 'string') {
      trimmed[key] = value.trim();
    }
    return trimmed;
  }, safe);
};

/**
 * Assign an adult passenger to the associatedAdultId of an infant.
 * @param {FlightOfferObj} flight_offer The initial flight offer
 * @param {{[infantId: string]: string}} heldPassengers a map linking infantId to associatedAdultId
 * @returns A copy of the original flight_offer with the updated infant
 */
export function set_infant_associated_adult_id(flight_offer, heldPassengers) {
  if (!heldPassengers || !flight_offer) {
    return flight_offer;
  }
  const { travelerPricings } = flight_offer;
  if (!travelerPricings) {
    return flight_offer;
  }
  return {
    ...flight_offer,
    travelerPricings: travelerPricings.map((pricing) => {
      const associatedAdultId = heldPassengers[pricing.travelerId];
      if (!associatedAdultId) {
        return pricing;
      }
      if (!is_infant(pricing)) {
        log('Trying to assign associatedAdultId for a non infant passenger');
        return pricing;
      }
      return {
        ...pricing,
        associatedAdultId,
      };
    }),
  };
}

/**
 * Italian label for a traveler type/count combination.
 * @param {object} traveler
 * @param {number} [traveler_quantity=1]
 * @returns {string}
 */
export function transcode_traveler_type(traveler, traveler_quantity = 1) {
  const plural = traveler_quantity !== 1;
  if (is_child(traveler)) {
    return plural ? 'bambini' : 'bambino';
  } else if (is_infant(traveler)) {
    return plural ? 'neonati' : 'neonato';
  } else if (is_teen(traveler)) {
    return plural ? 'adolescenti' : 'adolescente';
  }
  return plural ? 'adulti' : 'adulto';
}

/**
 * Age committed at search submission for a provider travelerId, or null.
 * Both providers echo the search traveler's 1-based position in the id
 * (Amadeus "2", Kyte "P2"), so the numeric tail keys straight into the map
 * frozen by the store's index_traveler_ages.
 *
 * @param {object} committed_traveler_ages travelerId -> age | null
 * @param {string|number} traveler_id
 * @returns {number|null}
 */
export const committed_age = (committed_traveler_ages, traveler_id) => {
  const ages = committed_traveler_ages || {};
  const age = ages[String(traveler_id).replace(/\D/g, '')];
  return typeof age === 'number' ? age : null;
};

/**
 * The committed age get_traveler_birth_range_dates actually pins the bounds to,
 * or null when the traveler keeps the multi-year band of their type label. The
 * form's helper texts read this so they never describe bounds nobody enforces.
 *
 * A committed age pins the birth date to the single year that makes the pax
 * exactly that age on the last departure. The type labels stay the fallback:
 * adults (18+) and any pax the occupancy never aged keep their multi-year band.
 * Infants are excluded: Kyte re-sequences them as INF1..INFn, so the id's
 * numeric tail keys some *other* traveler's age - occupancy
 * [adult, infant 1, infant 0] returns INF1/INF2, and INF2's tail "2" would
 * read the first infant's age 1, locking the 0-year-old out of their own
 * birth date. Their label band is already the exact 0-2y window.
 *
 * @param {object} traveler
 * @param {number|null} age age committed at search submission
 * @returns {number|null}
 */
export const enforced_age = (traveler, age) => {
  let pinned = typeof age === 'number' && age >= 0 && age < ADULT_AGE;
  if (is_infant(traveler)) {
    pinned = false;
  }
  if (!pinned) {
    return null;
  }
  return age;
};

/**
 * The age segment of a traveler's persistent-input key: the enforced age, or
 * '' so build_store_key drops it for travelers without one.
 *
 * @param {object} traveler
 * @param {object} committed_traveler_ages travelerId -> age | null
 * @param {string|number} traveler_id
 * @returns {number|string}
 */
export const store_key_age = (traveler, committed_traveler_ages, traveler_id) => {
  const age = enforced_age(
    traveler,
    committed_age(committed_traveler_ages, traveler_id)
  );
  if (age === null) {
    return '';
  }
  return age;
};

/**
 * Birth-date picker bounds for a given traveler. Anchored to the last
 * leg's departure so age requirements hold for the entire trip; today is
 * a last-resort fallback when the offer hasn't been normalized yet.
 *   - HELD_INFANT / SEATED_INFANT → (anchor - 2y + 1d) .. (anchor - 2d)
 *   - CHILD                       → (anchor - 12y - 1d) .. (anchor - 2d)
 *   - TEEN (12–16)                → (anchor - 16y - 1d) .. (anchor - 12y - 1d)
 *   - ADULT (default)             → 1900-01-01 .. (anchor - 12y - 2d),
 *                                   primo pax: (anchor - 18y - 2d)
 * @param {object} flight_offer
 * @param {object} traveler
 * @param {number} [traveler_index=-1] index in traveler_details; index 0 must be 18+
 * @param {number|null} [age=null] age committed at search submission
 * @returns {{min: string, max: string}}
 */
export function get_traveler_birth_range_dates(
  flight_offer,
  traveler,
  traveler_index = -1,
  age = null
) {
  const { lastDeparture = {} } = flight_offer || {};
  let departure_date = new Date();
  if (lastDeparture.at) {
    departure_date = lastDeparture.at;
  }

  const pinned_age = enforced_age(traveler, age);
  if (pinned_age !== null) {
    return get_birth_date_constraints({
      target_date: departure_date,
      age: pinned_age,
    });
  }

  if (is_infant(traveler)) {
    return {
      max: moment(departure_date).add(-2, 'd').format('YYYY-MM-DD'),
      min: moment(departure_date).add(-2, 'y').add(1, 'd').format('YYYY-MM-DD'),
    };
  }

  if (is_child(traveler)) {
    return {
      max: moment(departure_date).add(-2, 'd').format('YYYY-MM-DD'),
      min: moment(departure_date)
        .add(-12, 'y')
        .add(-1, 'd')
        .format('YYYY-MM-DD'),
    };
  }

  if (is_teen(traveler)) {
    return {
      max: moment(departure_date)
        .add(-12, 'y')
        .add(-1, 'd')
        .format('YYYY-MM-DD'),
      min: moment(departure_date)
        .add(-16, 'y')
        .add(-1, 'd')
        .format('YYYY-MM-DD'),
    };
  }

  // ADULT (default) — il primo pax deve necessariamente essere maggiorenne
  let years_offset = -12;
  if (traveler_index === 0) {
    years_offset = -18;
  }
  return {
    max: moment(departure_date)
      .add(years_offset, 'y')
      .add(-2, 'd')
      .format('YYYY-MM-DD'),
    min: '1900-01-01',
  };
}
